import { useState, useEffect, useCallback } from 'react'
import { useDebounce } from 'use-debounce'
import { Filters, SEARCH_DEBOUNCE_MS, UnifiedSearchTab } from '../constants'

interface SearchState {
  searchTerm: string
  setSearchTerm: (term: string) => void
  debouncedSearchTerm: string
  selectedTab: UnifiedSearchTab
  setSelectedTab: (tab: UnifiedSearchTab) => void
  filters: Filters
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  sortBy: string
  setSortBy: (sort: string) => void
  firstBitePage: number
  setFirstBitePage: (page: number) => void
  unmatchedPage: number
  setUnmatchedPage: (page: number) => void
  firstBiteDoorPage: number
  setFirstBiteDoorPage: (page: number) => void
  unmatchedDoorPage: number
  setUnmatchedDoorPage: (page: number) => void
  variantPage: number
  setVariantPage: (page: number) => void
  currentPage: number
  setCurrentPage: (page: number) => void
  resetPagination: () => void
}

interface UseUnifiedSearchStateProps {
  initialState: {
    searchTerm: string
    selectedTab: UnifiedSearchTab
    filters: Filters
    sortBy: string
    firstBitePage: number
    unmatchedPage: number
    firstBiteDoorPage: number
    unmatchedDoorPage: number
    variantPage: number
  }
}

export function useUnifiedSearchState({
  initialState,
}: UseUnifiedSearchStateProps): SearchState {
  // Search state
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm)
  const [debouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS)

  // Tab state
  const [selectedTab, setSelectedTab] = useState<UnifiedSearchTab>(
    initialState.selectedTab
  )

  // Filter and sort state
  const [filters, setFilters] = useState<Filters>(initialState.filters)
  const [sortBy, setSortBy] = useState(initialState.sortBy)

  // Pagination state
  const [firstBitePage, setFirstBitePage] = useState(initialState.firstBitePage)
  const [unmatchedPage, setUnmatchedPage] = useState(initialState.unmatchedPage)
  const [firstBiteDoorPage, setFirstBiteDoorPage] = useState(
    initialState.firstBiteDoorPage
  )
  const [unmatchedDoorPage, setUnmatchedDoorPage] = useState(
    initialState.unmatchedDoorPage
  )
  const [variantPage, setVariantPage] = useState(initialState.variantPage)

  // Derived state
  const currentPage =
    selectedTab === 'firstbite'
      ? firstBitePage
      : selectedTab === 'firstbitedoors'
        ? firstBiteDoorPage
        : selectedTab === 'unmatcheddoors'
          ? unmatchedDoorPage
          : selectedTab === 'variants'
            ? variantPage
            : unmatchedPage
  const setCurrentPage =
    selectedTab === 'firstbite'
      ? setFirstBitePage
      : selectedTab === 'firstbitedoors'
        ? setFirstBiteDoorPage
        : selectedTab === 'unmatcheddoors'
          ? setUnmatchedDoorPage
          : selectedTab === 'variants'
            ? setVariantPage
            : setUnmatchedPage

  // Reset pagination when search term changes
  const resetPagination = useCallback(() => {
    setFirstBitePage(1)
    setUnmatchedPage(1)
    setFirstBiteDoorPage(1)
    setUnmatchedDoorPage(1)
    setVariantPage(1)
  }, [])

  useEffect(() => {
    resetPagination()
  }, [debouncedSearchTerm, resetPagination])

  // Update state when initial state changes (from URL params)
  useEffect(() => {
    setSearchTerm(initialState.searchTerm)
    setSelectedTab(initialState.selectedTab)
    setFilters(initialState.filters)
    setSortBy(initialState.sortBy)
    setFirstBitePage(initialState.firstBitePage)
    setUnmatchedPage(initialState.unmatchedPage)
    setFirstBiteDoorPage(initialState.firstBiteDoorPage)
    setUnmatchedDoorPage(initialState.unmatchedDoorPage)
    setVariantPage(initialState.variantPage)
  }, [initialState])

  return {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    selectedTab,
    setSelectedTab,
    filters,
    setFilters,
    sortBy,
    setSortBy,
    firstBitePage,
    setFirstBitePage,
    unmatchedPage,
    setUnmatchedPage,
    firstBiteDoorPage,
    setFirstBiteDoorPage,
    unmatchedDoorPage,
    setUnmatchedDoorPage,
    variantPage,
    setVariantPage,
    currentPage,
    setCurrentPage,
    resetPagination,
  }
}
